import { useEffect, useState } from 'react'

import { usePaginatedFetch } from '../../hooks/usePaginatedFetch'

import { Listing } from './listing/Listing'

import { DEFAULT_PAGE_SIZE } from '../../constants/listing'

import {
  DisplayType,
  ListingDetailsProps,
  SliceResponse,
  SortDirectionType,
  SortType,
} from '../../types/listing'

export const ListingDetails = <T,>({
  endpoint,
  renderItem,
  pageSize = DEFAULT_PAGE_SIZE,
  defaultSortField = SortType.DATE,
  defaultSortDirection = SortDirectionType.ASC,
  sortOptions,
  displaySearch = false,
  searchPlaceholder,
  initialViewMode = DisplayType.LIST,
  tableColumns,
  additionalParams,
  refreshTrigger,
  onError,
  title,
  filter,
  gridItemClassName,
  mobileGridItemClassName,
}: ListingDetailsProps<T>) => {
  const [page, setPage] = useState(0)
  const [items, setItems] = useState<T[]>([])
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState(defaultSortField)
  const [sortDirection, setSortDirection] =
    useState<SortDirectionType>(defaultSortDirection)

  const additionalParamsKey = JSON.stringify(additionalParams ?? {})

  const { data, loading, error } = usePaginatedFetch<T>(
    endpoint,
    {
      page,
      size: pageSize,
      search,
      sort: sortField,
      direction: sortDirection,
      ...additionalParams,
    },
    refreshTrigger,
  )

  useEffect(() => {
    setPage(0)
  }, [refreshTrigger])

  useEffect(() => {
    setPage(0)
    setItems([])
  }, [additionalParamsKey])

  useEffect(() => {
    if (!data || data.pageable.pageNumber !== page) {
      return
    }

    setItems(previousItems => {
      if (page === 0) {
        return data.content
      }

      return [...previousItems, ...data.content]
    })
  }, [data, page])

  useEffect(() => {
    if (error) {
      onError?.(error)
    }
  }, [error, onError])

  const handleSearchChange = (value: string) => {
    if (value === search) {
      return
    }

    setSearch(value)
    setPage(0)
    setItems([])
  }

  const handleSortChange = (field: SortType, direction: SortDirectionType) => {
    setSortField(field)
    setSortDirection(direction)
    setPage(0)
    setItems([])
  }

  const handleLoadMore = () => {
    if (loading || !data || data.pageable.last) {
      return
    }

    setPage(previousPage => previousPage + 1)
  }

  const response: SliceResponse<T> = {
    content: items,
    pageable: {
      pageNumber: data?.pageable.pageNumber ?? page,
      size: data?.pageable.size ?? pageSize,
      last: data?.pageable.last ?? true,
    },
  }

  return (
    <Listing<T>
      response={response}
      renderItem={renderItem}
      onLoadMore={handleLoadMore}
      loading={loading}
      initialViewMode={initialViewMode}
      onSearchChange={handleSearchChange}
      displaySearch={displaySearch}
      searchPlaceholder={searchPlaceholder}
      sortOptions={sortOptions}
      currentSortField={sortField}
      currentSortDirection={sortDirection}
      onSortChange={handleSortChange}
      tableColumns={tableColumns}
      title={title}
      filter={filter}
      gridItemClassName={gridItemClassName}
      mobileGridItemClassName={mobileGridItemClassName}
    />
  )
}
